import appConfig from './app.json';

export interface ApiConfig {
  polygon: {
    baseUrl: string;
    apiKey: string;
    rateLimitMs: number;
    retryAttempts: number;
    retryDelayMs: number;
    timeout: number;
    stocksPerPage: number;
    maxPages: number;
    rateLimitDelayMs: number;
  };
}

export interface CacheConfig {
  durationMs: number;
  maxEntries: number;
  enableLocalStorage: boolean;
  queryCache: {
    storageKey: string;
    version: string;
    maxAgeMs: number;
    staleTimeMs: number;
    gcTimeMs: number;
    persistIntervalMs: number;
  };
}

export interface ExchangesConfig {
  us: {
    primary: string[];
    aliases: string[];
  };
}

export interface ChartConfig {
  timeIntervals: {
    daily: TimeIntervalConfig;
    weekly: TimeIntervalConfig;
    monthly: TimeIntervalConfig;
  };
  mockData: {
    baseDate: string;
    basePriceRange: {
      min: number;
      max: number;
    };
    variationRange: number;
  };
}

export interface TimeIntervalConfig {
  dataPoints: number;
  dateIncrement: number;
  incrementType: 'days' | 'weeks' | 'months';
}

export interface UIConfig {
  maxStocksAllowed: number;
}

export interface DevelopmentConfig {
  useMockData: boolean;
  enableApiLogging: boolean;
}

export interface AppConfig {
  api: ApiConfig;
  cache: CacheConfig;
  ui: UIConfig;
  exchanges: ExchangesConfig;
  chart: ChartConfig;
  development: DevelopmentConfig;
}

const createApiConfig = (baseConfig: AppConfig): ApiConfig => ({
  ...baseConfig.api,
  polygon: {
    ...baseConfig.api.polygon,
    apiKey: import.meta.env.VITE_POLYGON_API_KEY || baseConfig.api.polygon.apiKey,
  },
});

const createCacheConfig = (baseConfig: AppConfig): CacheConfig => ({
  ...baseConfig.cache,
  enableLocalStorage:
    import.meta.env.VITE_DISABLE_LOCALSTORAGE !== 'true'
      ? baseConfig.cache.enableLocalStorage
      : false,
});

const createDevelopmentConfig = (baseConfig: AppConfig): DevelopmentConfig => ({
  ...baseConfig.development,
  useMockData: import.meta.env.VITE_USE_MOCK_DATA === 'true',
});

const config: AppConfig = {
  ...(appConfig as AppConfig),
  api: createApiConfig(appConfig as AppConfig),
  cache: createCacheConfig(appConfig as AppConfig),
  development: createDevelopmentConfig(appConfig as AppConfig),
};

export { config };

export const getApiConfig = (): ApiConfig => config.api;
export const getCacheConfig = (): CacheConfig => config.cache;
export const getUIConfig = (): UIConfig => config.ui;
export const getExchangesConfig = (): ExchangesConfig => config.exchanges;
export const getChartConfig = (): ChartConfig => config.chart;
export const getDevelopmentConfig = (): DevelopmentConfig => config.development;
